<template>
	<div class="map-container">
		<div id="map"></div>
	</div>
</template>

<script>
import {useScriptStore} from "@/shared/stores";

export default {
	setup() {
		const scriptStore = useScriptStore();
		return {scriptStore};
	},
	name: "MapCluster",
	data() {
		return {
			map: null,
		}
	},
	props: {
		geojson: {
			type: Object,
			required: true,
		}
	},
	methods: {
		initMap() {

			this.scriptStore.loadScript("mapbox", () => {

				mapboxgl.accessToken = MAPBOX_PUBLIC_TOKEN;
				this.map = new mapboxgl.Map({
					container: 'map',
					style: 'mapbox://styles/paoloqromo/clkfjcuy5004e01qyanrw9oqg',
					center: [12.5674, 41.8719],
					zoom: 4.5
				});

				this.map.on('load', () => {

					this.addClusters();
					this.handleClusterEvents();

				});

			});

		},
		addClusters() {
			if(!this.map) return;

			this.map.addSource('businesses', {
				type: 'geojson',
				data: this.geojson,
				cluster: true,
				clusterMaxZoom: 14, // Max zoom to cluster points on
				clusterRadius: 50 // Radius of each cluster when clustering points (defaults to 50)
			});

			this.map.addLayer({
				id: 'clusters',
				type: 'circle',
				source: 'businesses',
				filter: ['has', 'point_count'],
				paint: {
					//   * 14px circles when point count is less than 10
					//   * 20px circles when point count is between 10 and 40
					//   * 30px circles when point count is between 40 and 150
					//   * 40px circles when point count is greater than 150
					'circle-color': [
						'step',
						['get', 'point_count'],
						'#FDDE00',
						10,
						'#FC7502',
						40,
						'#FC2291',
						150,
						'#E3216B'
					],
					'circle-radius': [
						'step',
						['get', 'point_count'],
						14,
						10,
						20,
						40,
						30,
						150,
						40
					]
				}
			});

			this.map.addLayer({
				id: 'cluster-count',
				type: 'symbol',
				source: 'businesses',
				filter: ['has', 'point_count'],
				layout: {
					'text-field': ['get', 'point_count_abbreviated'],
					'text-font': ['Montserrat SemiBold'],
					'text-size': 13
				}
			});

			this.map.addLayer({
				id: 'unclustered-point',
				type: 'circle',
				source: 'businesses',
				filter: ['!', ['has', 'point_count']],
				paint: {
					'circle-color': '#2CECBE',
					'circle-radius': 4,
					'circle-stroke-width': 2,
					'circle-stroke-color': '#0752A3'
				}
			});

		},
		handleClusterEvents() {


			this.map.on('click', 'clusters', (e) => {
				const features = this.map.queryRenderedFeatures(e.point, {
					layers: ['clusters']
				});
				const clusterId = features[0].properties.cluster_id;
				this.map.getSource('businesses').getClusterExpansionZoom(
					clusterId,
					(err, zoom) => {
						if (err) return;

						this.map.easeTo({
							center: features[0].geometry.coordinates,
							zoom: zoom
						});
					}
				);
			});

			//popup on single point
			this.map.on('click', 'unclustered-point', (e) => {
				const coordinates = e.features[0].geometry.coordinates.slice();
				const name = e.features[0].properties.businessName;
				const id = e.features[0].properties.businessId;

				// Ensure that if the this.map is zoomed out such that
				// multiple copies of the feature are visible, the
				// popup appears over the copy being pointed to.
				while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
					coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
				}

				new mapboxgl.Popup()
					.setLngLat(coordinates)
					.setHTML(
						`<b>${name}</b><br>Business ID: #${id}`
					)
					.addTo(this.map);
			});

			this.map.on('mouseenter', 'clusters', () => {
				this.map.getCanvas().style.cursor = 'pointer';
			});
			this.map.on('mouseleave', 'clusters', () => {
				this.map.getCanvas().style.cursor = '';
			});

			this.map.on('mouseenter', 'unclustered-point', () => {
				this.map.getCanvas().style.cursor = 'pointer';
			});
			this.map.on('mouseleave', 'unclustered-point', () => {
				this.map.getCanvas().style.cursor = '';
			});
		},
	},
	mounted() {
		this.initMap();
	},
	watch: {
		geojson(newVal, oldVal) {
			if(this.map && newVal !== oldVal) {
				let currSource = this.map.getSource('businesses');
				if(currSource) {
					currSource.setData(newVal);
				}
			}
		}
	}
}
</script>

<style lang="scss">
.map-container {
	a {
		text-decoration: none;

		&::after {
			display: none;
		}
	}

	.mapboxgl-ctrl-logo {
		display: none;
	}
	.mapboxgl-ctrl-attrib {
		display: none;
	}
}
</style>

<style scoped lang="scss">
.map-container {
	width: 100%;
	position: relative;

	#map {
		width: 100%;
		min-height: 450px;
		border-radius: 13px;
	}
}
</style>